import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import { EmojiEvents as TrophyIcon } from "@mui/icons-material";
import { HOL, holFadeUp } from "../UserSide/pages/holTheme";

const MEDALS = {
  1: { color: "#ffd54f", glow: "rgba(255,213,79,0.35)" },
  2: { color: "#cfd8dc", glow: "rgba(207,216,220,0.25)" },
  3: { color: "#ffab76", glow: "rgba(255,171,118,0.25)" },
};

/**
 * Ranked Higher or Lower standings (from the server's final results / live leaderboard).
 * Each entry: { rank, device_id, display_name, score, total_rounds, plays }.
 */
const HolLeaderboard = ({
  entries = [],
  highlightDeviceId = null,
  maxRows = null,
  dense = false,
  animate = true,
  emptyText = "No hot seat runs were completed in this session.",
}) => {
  const rows = maxRows ? entries.slice(0, maxRows) : entries;

  if (!rows.length) {
    return (
      <Typography sx={{ color: HOL.muted, textAlign: "center", py: dense ? 1.5 : 3, fontSize: "0.92rem" }}>
        {emptyText}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: dense ? 0.75 : 1 }}>
      {rows.map((entry, i) => {
        const medal = MEDALS[entry.rank];
        const isSelf =
          !!highlightDeviceId &&
          !!entry.device_id &&
          String(entry.device_id) === String(highlightDeviceId);
        const rounds = Number(entry.total_rounds) || 0;
        const pct = rounds > 0 ? Math.min(100, Math.round((entry.score / rounds) * 100)) : 0;
        return (
          <Box
            key={`${entry.device_id || entry.display_name}-${i}`}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: dense ? 1.25 : 1.75,
              px: dense ? 1.25 : 2,
              py: dense ? 0.85 : 1.25,
              borderRadius: dense ? "12px" : "16px",
              textAlign: "left",
              bgcolor: isSelf
                ? "rgba(168,224,99,0.12)"
                : medal
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(255,255,255,0.025)",
              border: `1px solid ${
                isSelf ? `${HOL.higher}66` : medal ? `${medal.color}33` : HOL.panelBorder
              }`,
              boxShadow: medal && entry.rank === 1 ? `0 0 24px ${medal.glow}` : "none",
              animation: animate ? `${holFadeUp} 0.45s ease both` : "none",
              animationDelay: animate ? `${Math.min(i, 10) * 0.08}s` : undefined,
            }}
          >
            <Box
              sx={{
                width: dense ? 30 : 38,
                height: dense ? 30 : 38,
                flexShrink: 0,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                fontFamily: HOL.fontDisplay,
                fontWeight: 900,
                fontSize: dense ? "0.85rem" : "1rem",
                color: medal ? "#141414" : "#fff",
                background: medal
                  ? `radial-gradient(circle at 35% 30%, #fff8, ${medal.color} 60%)`
                  : "rgba(255,255,255,0.08)",
              }}
            >
              {entry.rank === 1 ? <TrophyIcon sx={{ fontSize: dense ? 16 : 20 }} /> : entry.rank}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                <Typography
                  noWrap
                  sx={{
                    fontWeight: 800,
                    fontSize: dense ? "0.92rem" : "1.05rem",
                    color: medal ? medal.color : "#fff",
                  }}
                >
                  {entry.display_name}
                </Typography>
                {isSelf ? (
                  <Chip
                    label="You"
                    size="small"
                    sx={{ height: 18, fontSize: "0.65rem", fontWeight: 800, bgcolor: HOL.higher, color: "#0a1206" }}
                  />
                ) : null}
                {entry.plays > 1 ? (
                  <Typography component="span" sx={{ color: HOL.muted, fontSize: "0.72rem" }}>
                    best of {entry.plays}
                  </Typography>
                ) : null}
              </Box>
              {!dense ? (
                <Box sx={{ mt: 0.75, height: 5, borderRadius: 999, bgcolor: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                  <Box
                    sx={{
                      height: "100%",
                      width: `${pct}%`,
                      borderRadius: 999,
                      background: `linear-gradient(90deg, ${HOL.higherDeep}, ${HOL.higher})`,
                      transition: "width 0.8s cubic-bezier(0.22,1,0.36,1)",
                    }}
                  />
                </Box>
              ) : null}
            </Box>

            <Typography
              sx={{
                fontFamily: HOL.fontDisplay,
                fontWeight: 900,
                fontSize: dense ? "1rem" : "1.35rem",
                color: HOL.gold,
                whiteSpace: "nowrap",
              }}
            >
              {entry.score}
              <Typography component="span" sx={{ color: HOL.muted, fontWeight: 600, fontSize: "0.8em", ml: 0.4 }}>
                / {rounds}
              </Typography>
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default HolLeaderboard;
